import { QuerySourceType, } from '../../types/querySources';
export const isAiComposerChartArtifactConfig = (config) => typeof config === 'object' &&
    config !== null &&
    'source' in config &&
    config.source === 'composer' &&
    'schemaVersion' in config &&
    config.schemaVersion === 1 &&
    'queries' in config &&
    Array.isArray(config.queries) &&
    'terminalNodeId' in config &&
    typeof config.terminalNodeId === 'string' &&
    'lastQueryUuid' in config &&
    typeof config.lastQueryUuid === 'string';
const NODE_ID_MAX_LENGTH = 63;
// Suffix grows deterministically (8, 12, 16… hex chars) until the id is free.
const getCopiedNodeId = (nodeId, queryUuid, takenIds) => {
    const hex = queryUuid.replace(/[^a-zA-Z0-9]/g, '');
    if (nodeId.endsWith(`_${hex.slice(0, 8)}`))
        return nodeId;
    for (let length = 8;; length += 4) {
        const suffix = `_${hex.slice(0, length)}`;
        const candidate = `${nodeId.slice(0, NODE_ID_MAX_LENGTH - suffix.length)}${suffix}`;
        if (!takenIds.has(candidate) || length >= hex.length)
            return candidate;
    }
};
/**
 * Builds the stored pipeline of a composer artifact: this run's nodes plus
 * every reused node (and its transitive reads) copied from earlier versions.
 */
export const buildComposerArtifactPipeline = ({ queries, submissions, earlierPipelines, }) => {
    const earlierNodesByQueryUuid = new Map();
    earlierPipelines.forEach((pipeline) => {
        const { nodeResults } = pipeline;
        if (!nodeResults)
            return;
        pipeline.queries.forEach((node) => {
            const result = node.nodeId ? nodeResults[node.nodeId] : undefined;
            if (result && !earlierNodesByQueryUuid.has(result.queryUuid)) {
                earlierNodesByQueryUuid.set(result.queryUuid, {
                    node,
                    pipeline,
                });
            }
        });
    });
    const submittedNodeIds = new Set(submissions.map((submission) => submission.nodeId));
    const takenIds = new Set(submittedNodeIds);
    const copiedIdsByQueryUuid = new Map();
    const copiedNodes = [];
    const copiedNodeResults = {};
    // Resolves a reference to a copied node id, or null when nothing earlier owns it.
    const copyNode = (queryUuid) => {
        const copiedId = copiedIdsByQueryUuid.get(queryUuid);
        if (copiedId)
            return copiedId;
        const earlier = earlierNodesByQueryUuid.get(queryUuid);
        if (!earlier?.node.nodeId)
            return null;
        const nodeId = getCopiedNodeId(earlier.node.nodeId, queryUuid, takenIds);
        takenIds.add(nodeId);
        copiedIdsByQueryUuid.set(queryUuid, nodeId);
        let node = { ...earlier.node, nodeId };
        if (node.sourceType === QuerySourceType.DUCKDB && node.references) {
            const entries = Array.isArray(node.references)
                ? node.references.map((reference) => [reference, reference])
                : Object.entries(node.references);
            node = {
                ...node,
                references: Object.fromEntries(entries.map(([tableName, reference]) => {
                    const localQueryUuid = earlier.pipeline.nodeResults?.[reference]
                        ?.queryUuid;
                    return [
                        tableName,
                        copyNode(localQueryUuid ?? reference) ?? reference,
                    ];
                })),
            };
        }
        copiedNodes.push(node);
        copiedNodeResults[nodeId] = { queryUuid };
        return nodeId;
    };
    const submittedQueries = queries.map((node) => {
        if (node.sourceType !== QuerySourceType.DUCKDB ||
            !node.references ||
            Array.isArray(node.references)) {
            return node;
        }
        return {
            ...node,
            references: Object.fromEntries(Object.entries(node.references).map(([tableName, reference]) => [
                tableName,
                submittedNodeIds.has(reference)
                    ? reference
                    : (copyNode(reference) ?? reference),
            ])),
        };
    });
    return {
        queries: [...copiedNodes, ...submittedQueries],
        nodeResults: {
            ...copiedNodeResults,
            ...Object.fromEntries(submissions.map((submission) => [
                submission.nodeId,
                { queryUuid: submission.queryUuid },
            ])),
        },
    };
};
//# sourceMappingURL=composerArtifact.js.map